import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import exchangeService from '../../services/exchangeService';
import { LANGUAGE_OPTIONS } from './exchangeConstants';
import ExchangeProfilePhoto from './ExchangeProfilePhoto';
import { FaUserFriends, FaSpinner, FaArrowLeft } from 'react-icons/fa';
import '../../ExchangeProfileForm.css';

const ExchangeProfileForm = () => {
  const [isEditMode, setIsEditMode] = useState(false);
  const [currentAge, setCurrentAge] = useState(null);

  const [nickname, setNickname] = useState('');
  const [birthDate, setBirthDate] = useState('');
  const [gender, setGender] = useState('');
  const [bio, setBio] = useState('');
  const [nativeLanguages, setNativeLanguages] = useState([]);
  const [learningLanguages, setLearningLanguages] = useState([]);

  // 사진 관련 state. photoFileId는 업로드가 성공하면 백엔드가 돌려주는 UserFile id이고,
  // 이것을 실제 프로필 저장 요청에 함께 보내야 사진이 연결된다.
  const [photoFileId, setPhotoFileId] = useState(null);
  // 새로 고른(아직 업로드 전이거나 업로드 직후) 파일의 로컬 미리보기다. createObjectURL로
  // 만든 blob: url이라 인증 헤더 없이도 바로 보인다.
  const [photoPreviewUrl, setPhotoPreviewUrl] = useState('');
  // 서버에 이미 저장된 사진의 API 경로(/api/exchange/profile/photo/{id}). 이 경로는 로그인이 필요해서
  // 일반 <img src>로는 못 보여주고, ExchangeProfilePhoto 컴포넌트(인증 blob fetch)를 통해서만 보여준다.
  const [existingPhotoUrl, setExistingPhotoUrl] = useState('');
  const [photoUploading, setPhotoUploading] = useState(false);
  const [photoError, setPhotoError] = useState('');

  const [initialLoading, setInitialLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // 허용하는 확장자와 최대 용량(5MB). 백엔드 ExchangeProfileServiceImpl.uploadPhoto()의
  // MAX_PHOTO_SIZE_BYTES(5MB) 제한과 또같이 맞춰놓은 값이다. 서버에서 또 한번 막아주지만,
  // 큰 파일을 업로드하고 나서야 실패하는 것보다 미리 막아주는 게 경험이 더 좋다.
  const ALLOWED_PHOTO_EXTENSIONS = ['jpg', 'jpeg', 'png', 'webp'];
  const MAX_PHOTO_SIZE_BYTES = 5 * 1024 * 1024;

  useEffect(() => {
    fetchMyProfile();
  }, []);

  const fetchMyProfile = async () => {
    try {
      setInitialLoading(true);
      const response = await exchangeService.getMyProfile();

      // 서버가 204(No Content)를 주면 아직 프로필이 없다는 뜻이다. 이 경우는 신규 등록 화면으로 그대로 둔다.
      if (response.status === 204 || !response.data) {
        setIsEditMode(false);
      } else {
        const profile = response.data;
        setIsEditMode(true);
        setNickname(profile.nickname || '');
        setGender(profile.gender || '');
        setBio(profile.bio || '');
        setNativeLanguages(profile.nativeLanguages || []);
        setLearningLanguages(profile.learningLanguages || []);
        setCurrentAge(profile.age != null ? profile.age : null);
        // 이미 등록된 사진이 있으면 photoFileId는 그대로 유지해서 사용자가 새 사진을 안 올리면
        // 기존 사진이 그대로 유지되게 하고, 미리보기는 인증이 필요한 existingPhotoUrl로 따로 관리한다.
        if (profile.photoFileId) {
          setPhotoFileId(profile.photoFileId);
          setExistingPhotoUrl(profile.photoUrl || '');
        }
        // 주의: 서버는 개인정보 보호를 위해 생년월일 원본을 내려주지 않고 나이(age)만 내려준다.
        // 그래서 수정할 때는 생년월일을 다시 입력해야 한다. birthDate는 일부러 비워둔다.
      }
    } catch (err) {
      console.error('프로필 조회 오류:', err);
      setErrorMessage('프로필 정보를 불러오는 중 오류가 발생했다.');
    } finally {
      setInitialLoading(false);
    }
  };

  const toggleLanguage = (code, listName) => {
    if (listName === 'native') {
      setNativeLanguages((prev) =>
        prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]
      );
    } else {
      setLearningLanguages((prev) =>
        prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]
      );
    }
  };

  const calculateAge = (dateString) => {
    const birth = new Date(dateString);
    const today = new Date();
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return age;
  };

  // 사진을 고르면 바로 이 함수가 실행된다. 확장자와 용량을 먼저 프론트에서 검사해서
  // 잘못된 파일을 지업하면 서버까지 안 보내고 바로 메시지를 보여준다. 통과하면 바로
  // 업로드 요청(POST /api/exchange/profile/photo)을 보내고, 성공하면 받은 photoFileId를
  // 저장해둔다(이 id를 프로필 저장 요청에 함께 보내야 실제로 사진이 연결된다).
  const handlePhotoChange = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) {
      return;
    }

    setPhotoError('');

    const extension = file.name.split('.').pop().toLowerCase();
    if (!ALLOWED_PHOTO_EXTENSIONS.includes(extension)) {
      setPhotoError('jpg, jpeg, png, webp 확장자의 이미지만 업로드할 수 있다.');
      e.target.value = '';
      return;
    }
    if (file.size > MAX_PHOTO_SIZE_BYTES) {
      setPhotoError('사진 파일은 5MB를 넘을 수 없다.');
      e.target.value = '';
      return;
    }

    // 업로드가 완료되기 전에도 미리보기를 바로 보여줘서 사용자가 어떤 사진을 고르는 지 바로 확인할 수 있게 한다.
    // 새 파일을 고르면, 더 이상 기존 사진(existingPhotoUrl)이 아니라 방금 고른 파일의 로컬 미리보기가 우선되어야 하므로 비운다.
    setExistingPhotoUrl('');
    const localPreviewUrl = URL.createObjectURL(file);
    setPhotoPreviewUrl(localPreviewUrl);

    try {
      setPhotoUploading(true);
      const response = await exchangeService.uploadProfilePhoto(file);
      setPhotoFileId(response.data.photoFileId);
    } catch (err) {
      console.error('사진 업로드 오류:', err);
      const serverMessage = err.response && err.response.data && err.response.data.message;
      setPhotoError(serverMessage || '사진 업로드 중 오류가 발생했다.');
      setPhotoPreviewUrl('');
      setPhotoFileId(null);
    } finally {
      setPhotoUploading(false);
    }
  };

  const validateForm = () => {
    if (!nickname.trim()) {
      return '닉네임을 입력해달라.';
    }
    if (!birthDate) {
      return '생년월일을 입력해달라.';
    }
    const age = calculateAge(birthDate);
    if (age < 19) {
      return '언어교환 기능은 만 19세 이상만 이용할 수 있다.';
    }
    if (nativeLanguages.length === 0) {
      return '도와줄 수 있는 언어(모국어)를 1개 이상 선택해달라.';
    }
    if (learningLanguages.length === 0) {
      return '배우고 싶은 언어를 1개 이상 선택해달라.';
    }
    return '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage('');
    setSuccessMessage('');

    const validationError = validateForm();
    if (validationError) {
      setErrorMessage(validationError);
      return;
    }

    try {
      setSaving(true);
      const requestData = {
        nickname: nickname.trim(),
        birthDate: birthDate,
        gender: gender || null,
        bio: bio || null,
        // 업로드가 성공하면 handlePhotoChange에서 photoFileId가 채워진다. 사진을 안 고르면
        // 기존 사진이 있을 경우 fetchMyProfile이 미리 채워둔 값이 그대로 들어가서 사진이 유지된다.
        photoFileId: photoFileId,
        nativeLanguages: nativeLanguages,
        learningLanguages: learningLanguages,
      };

      const response = await exchangeService.saveProfile(requestData);
      setSuccessMessage(isEditMode ? '프로필이 수정되었다.' : '프로필이 등록되었다.');
      setIsEditMode(true);
      setCurrentAge(response.data.age);
      setBirthDate('');
    } catch (err) {
      console.error('프로필 저장 오류:', err);
      const serverMessage = err.response && err.response.data && err.response.data.message;
      setErrorMessage(serverMessage || '프로필 저장 중 오류가 발생했다.');
    } finally {
      setSaving(false);
    }
  };

  if (initialLoading) {
    return (
      <div className="exchange-loading">
        <FaSpinner className="exchange-spinner" />
        <p>불러오는 중이다...</p>
      </div>
    );
  }

  return (
    <div className="exchange-profile-container">
      {/* 이 화면은 /profile(통합 프로필)의 "수정하기" 버튼으로만 들어오는 독립 수정 화면이다.
          그래서 상대찾기/매칭요청/채팅함 탭이 대는 ExchangeNav를 안 붙이고, 대신 프로필로
          돌아가는 간단한 뒤로가기 링크만 놓는다. */}
      <Link to="/profile" className="exchange-profile-back-link">
        <FaArrowLeft /> 프로필로 돌아가기
      </Link>
      <h1 className="exchange-title">
        <FaUserFriends /> {isEditMode ? '언어교환 프로필 수정' : '언어교환 프로필 등록'}
      </h1>

      {isEditMode && currentAge !== null && (
        <p className="exchange-current-age">현재 등록된 나이: 만 {currentAge}세</p>
      )}

      {errorMessage && <div className="exchange-error">{errorMessage}</div>}
      {successMessage && <div className="exchange-success">{successMessage}</div>}

      <form onSubmit={handleSubmit} className="exchange-form">
        <div className="exchange-form-group">
          <label htmlFor="photo">프로필 사진 (선택)</label>
          <div className="exchange-photo-upload-row">
            <div className="exchange-photo-preview">
              {photoPreviewUrl ? (
                <img src={photoPreviewUrl} alt="프로필 미리보기" />
              ) : existingPhotoUrl ? (
                <ExchangeProfilePhoto photoUrl={existingPhotoUrl} size={80} alt="프로필 미리보기" />
              ) : (
                <FaUserFriends className="exchange-photo-placeholder-icon" />
              )}
            </div>
            <div>
              <input
                id="photo"
                type="file"
                accept=".jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp"
                onChange={handlePhotoChange}
                disabled={photoUploading}
              />
              <p className="exchange-field-note">jpg, jpeg, png, webp / 최대 5MB. 선택하지 않아도 등록은 가능하다.</p>
              {photoUploading && <p className="exchange-field-note">업로드 중이다...</p>}
              {photoError && <div className="exchange-error">{photoError}</div>}
            </div>
          </div>
        </div>

        <div className="exchange-form-group">
          <label htmlFor="nickname">닉네임 *</label>
          <input
            id="nickname"
            type="text"
            value={nickname}
            onChange={(e) => setNickname(e.target.value)}
            placeholder="예: Minsu_Kim"
            maxLength={30}
          />
        </div>

        <div className="exchange-form-group">
          <label htmlFor="birthDate">
            생년월일 * {isEditMode && <span className="exchange-hint">(수정할 때마다 다시 입력해야 한다)</span>}
          </label>
          <input
            id="birthDate"
            type="date"
            value={birthDate}
            onChange={(e) => setBirthDate(e.target.value)}
          />
          <p className="exchange-field-note">
            만 19세 이상만 언어교환 기능을 쓸 수 있다. 생년월일은 나이 계산에만 쓰이고 다른 사람에게 공개되지 않는다.
          </p>
        </div>

        <div className="exchange-form-group">
          <label htmlFor="gender">성별 (선택)</label>
          <select id="gender" value={gender} onChange={(e) => setGender(e.target.value)}>
            <option value="">선택 안 함</option>
            <option value="남성">남성</option>
            <option value="여성">여성</option>
          </select>
        </div>

        <div className="exchange-form-group">
          <label htmlFor="bio">자기소개 (선택)</label>
          <textarea
            id="bio"
            value={bio}
            onChange={(e) => setBio(e.target.value)}
            placeholder="예: 한국어 원어민이고 영어를 재미있게 배우고 싶다."
            maxLength={300}
            rows={4}
          />
        </div>

        <div className="exchange-form-group">
          <label>도와줄 수 있는 언어 (모국어) *</label>
          <p className="exchange-field-note">내가 잘하는 언어를 골라주면, 그 언어를 배우고 싶은 사람에게 내가 보여진다.</p>
          <div className="exchange-language-grid">
            {LANGUAGE_OPTIONS.map((lang) => (
              <label key={`native-${lang.code}`} className="exchange-language-checkbox">
                <input
                  type="checkbox"
                  checked={nativeLanguages.includes(lang.code)}
                  onChange={() => toggleLanguage(lang.code, 'native')}
                />
                {lang.label}
              </label>
            ))}
          </div>
        </div>

        <div className="exchange-form-group">
          <label>배우고 싶은 언어 *</label>
          <p className="exchange-field-note">내가 배우고 싶은 언어를 골라주면, 그 언어가 모국어인 사람이 나에게 우선 보여진다.</p>
          <div className="exchange-language-grid">
            {LANGUAGE_OPTIONS.map((lang) => (
              <label key={`learning-${lang.code}`} className="exchange-language-checkbox">
                <input
                  type="checkbox"
                  checked={learningLanguages.includes(lang.code)}
                  onChange={() => toggleLanguage(lang.code, 'learning')}
                />
                {lang.label}
              </label>
            ))}
          </div>
        </div>

        <button type="submit" className="exchange-submit-button" disabled={saving || photoUploading}>
          {saving ? '저장 중이다...' : isEditMode ? '수정하기' : '등록하기'}
        </button>
      </form>
    </div>
  );
};

export default ExchangeProfileForm;
